// Weather tab — sub-tabs:
//   METAR  — live, wired to /api/wx/metar
//   TAF    — live, wired to /api/wx/taf
//   MOS    — LAMP 38-hour bulletin via /api/wx/lamp (shipped 2026-05-26)
//   Daily  — stubbed (NWS api.weather.gov forecast)
//   Winds  — stubbed (aviationweather.gov windsTemps)
//
// When the requested airport has no METAR or TAF on file (most small
// fields don't), we ask the API for the nearest stations within a
// radius and surface them ForeFlight-style. The user always sees
// SOMETHING useful even at an unstaffed strip.

import { useState } from 'react';
import { useFetchUrl } from '../../hooks/useFetch';
import { plainTextTaf, type TafEnvelope } from '../../lib/taf';
import {
  formatZuluWithLocal,
  formatZuluRangeWithLocal,
} from '../../lib/timeFormat';

type WxKey = 'metar' | 'taf' | 'mos' | 'daily' | 'winds';

const SUBTABS: Array<{ key: WxKey; label: string }> = [
  { key: 'metar', label: 'METAR' },
  { key: 'taf', label: 'TAF' },
  { key: 'mos', label: 'MOS' },
  { key: 'daily', label: 'Daily' },
  { key: 'winds', label: 'Winds' },
];

interface MetarRow {
  icaoId?: string;
  name?: string;
  lat?: number;
  lon?: number;
  reportTime?: string;
  rawOb?: string;
  rawMETAR?: string;
  wdir?: number | string;
  wspd?: number;
  wgst?: number;
  visib?: number | string;
  altim?: number;
  temp?: number;
  dewp?: number;
  clouds?: Array<{ cover?: string; base?: number }>;
  _distNm?: number;
}

interface TafRow {
  icaoId?: string;
  name?: string;
  lat?: number;
  lon?: number;
  issueTime?: string;
  rawTAF?: string;
  rawOb?: string;
  validTimeFrom?: number;
  validTimeTo?: number;
  fcsts?: Array<Record<string, unknown>>;
  _distNm?: number;
}

interface WxEnvelope<T> {
  station: T[];
  nearby: T[];
  meta?: { ident?: string; nearbyRadiusNm?: number };
}

interface Props {
  ident: string;
  /** Airport coordinates. When provided, the API can return nearest stations as a fallback. */
  lat?: number;
  lon?: number;
}

export function WeatherTab({ ident, lat, lon }: Props) {
  const [sub, setSub] = useState<WxKey>('metar');
  return (
    <div>
      <nav role="tablist" className="flex border-b border-cockpit-700 sticky top-0 bg-cockpit-800/95 backdrop-blur z-10">
        {SUBTABS.map((s) => (
          <button
            key={s.key}
            role="tab"
            aria-selected={sub === s.key}
            onClick={() => setSub(s.key)}
            className={[
              'flex-1 text-[11px] py-2 px-2 transition-colors whitespace-nowrap',
              sub === s.key
                ? 'text-ember-400 border-b-2 border-ember-500'
                : 'text-cockpit-300 hover:text-cockpit-100 border-b-2 border-transparent',
            ].join(' ')}
          >
            {s.label}
          </button>
        ))}
      </nav>
      <div className="px-4 py-3 text-sm">
        {sub === 'metar' && <MetarPanel ident={ident} lat={lat} lon={lon} />}
        {sub === 'taf' && <TafPanel ident={ident} lat={lat} lon={lon} />}
        {sub === 'mos' && <MosPanel ident={ident} />}
        {sub === 'daily' && <ComingSoon source="NWS api.weather.gov forecast for the airport's gridpoint — 7-day plain-language outlook." />}
        {sub === 'winds' && <ComingSoon source="aviationweather.gov windsTemps endpoint — winds + temps aloft at FL060/090/120/180/etc." />}
      </div>
    </div>
  );
}

function buildWxUrl(kind: 'metar' | 'taf', ident: string, lat?: number, lon?: number): string {
  const params = new URLSearchParams({ ident });
  if (lat != null && lon != null && Number.isFinite(lat) && Number.isFinite(lon)) {
    params.set('lat', String(lat));
    params.set('lon', String(lon));
  }
  return `/api/wx/${kind}?${params.toString()}`;
}

function MetarPanel({ ident, lat, lon }: { ident: string; lat?: number; lon?: number }) {
  const url = buildWxUrl('metar', ident, lat, lon);
  const { data, error, loading } = useFetchUrl<WxEnvelope<MetarRow>>(url);
  if (loading) return <Loading />;
  if (error) return <ErrorMsg msg={error} />;
  const station = data?.station ?? [];
  const nearby = data?.nearby ?? [];

  if (station.length === 0) {
    if (nearby.length === 0) {
      return (
        <NoData
          kind="METAR"
          hint={lat != null && lon != null
            ? 'No nearby METARs within 75 NM either.'
            : "Most small fields don't issue METARs."}
        />
      );
    }
    return (
      <div className="space-y-3">
        <div className="text-xs text-cockpit-300">
          No METAR for <span className="font-mono">{ident}</span>.
          Showing the {nearby.length} nearest station{nearby.length === 1 ? '' : 's'}:
        </div>
        {nearby.map((m) => (
          <NearbyMetar key={m.icaoId ?? Math.random()} metar={m} />
        ))}
      </div>
    );
  }

  // Station has its own METAR. Render it; surface a small "X others nearby" hint.
  const m = station[0];
  return (
    <div className="space-y-3">
      <div className="bg-cockpit-700/30 border border-cockpit-700 rounded-md px-3 py-2 font-mono text-xs leading-snug whitespace-pre-wrap">
        {m.rawOb || m.rawMETAR || '—'}
      </div>
      <Decoded metar={m} />
      <div className="flex items-center justify-between text-[10px] text-cockpit-400">
        <span>Reported: {formatZuluWithLocal(m.reportTime)}</span>
        {nearby.length > 0 && <span>{nearby.length} nearby</span>}
      </div>
    </div>
  );
}

function NearbyMetar({ metar }: { metar: MetarRow }) {
  const id = metar.icaoId ?? '???';
  const dist = metar._distNm != null ? `${metar._distNm} nm` : '';
  const name = metar.name ? metar.name.replace(/, US$/, '') : '';
  return (
    <div className="space-y-1">
      <div className="flex items-baseline justify-between">
        <span className="font-mono text-sm text-cockpit-100">{id}</span>
        <span className="text-[10px] text-cockpit-400 truncate ml-2">{name} {dist && `· ${dist}`}</span>
      </div>
      <div className="bg-cockpit-700/30 border border-cockpit-700 rounded-md px-3 py-2 font-mono text-[11px] leading-snug whitespace-pre-wrap">
        {metar.rawOb || metar.rawMETAR || '—'}
      </div>
      <Decoded metar={metar} />
    </div>
  );
}

function Decoded({ metar }: { metar: MetarRow }) {
  const rows: Array<[string, string | null]> = [
    ['Wind', metar.wdir != null ? `${String(metar.wdir).padStart(3, '0')}° at ${metar.wspd ?? 0} kt${metar.wgst ? ` G ${metar.wgst}` : ''}` : null],
    ['Visibility', metar.visib != null ? (typeof metar.visib === 'string' ? metar.visib : `${metar.visib} sm`) : null],
    ['Sky', formatSky(metar)],
    ['Temp / Dew', metar.temp != null ? `${metar.temp}°C / ${metar.dewp ?? '—'}°C` : null],
    ['Altimeter', metar.altim != null ? `${(metar.altim * 0.029530).toFixed(2)}" Hg` : null],
  ].filter(([, v]) => v != null) as Array<[string, string]>;

  if (rows.length === 0) return null;
  return (
    <div className="bg-cockpit-700/20 border border-cockpit-700 rounded-md px-3 py-2 space-y-0.5">
      {rows.map(([k, v]) => (
        <div key={k} className="flex items-center justify-between text-xs">
          <span className="text-cockpit-300">{k}</span>
          <span className="font-mono text-cockpit-100">{v}</span>
        </div>
      ))}
    </div>
  );
}

function formatSky(metar: MetarRow): string | null {
  const clouds = metar.clouds;
  if (!Array.isArray(clouds) || clouds.length === 0) return null;
  return clouds
    .map((c) => {
      if (!c || !c.cover) return null;
      const base = c.base != null ? ` ${c.base.toLocaleString()}'` : '';
      return `${c.cover}${base}`;
    })
    .filter(Boolean)
    .join(', ');
}

function TafPanel({ ident, lat, lon }: { ident: string; lat?: number; lon?: number }) {
  const url = buildWxUrl('taf', ident, lat, lon);
  const { data, error, loading } = useFetchUrl<WxEnvelope<TafRow>>(url);
  if (loading) return <Loading />;
  if (error) return <ErrorMsg msg={error} />;
  const station = data?.station ?? [];
  const nearby = data?.nearby ?? [];

  if (station.length === 0) {
    if (nearby.length === 0) {
      return (
        <NoData
          kind="TAF"
          hint={lat != null && lon != null
            ? "No nearby TAFs within 200 NM either. TAFs are only issued at ~700 US fields."
            : "Most small fields don't issue TAFs."}
        />
      );
    }
    return (
      <div className="space-y-3">
        <div className="text-xs text-cockpit-300">
          No TAF for <span className="font-mono">{ident}</span>.
          Showing the {nearby.length} nearest TAF station{nearby.length === 1 ? '' : 's'}:
        </div>
        {nearby.map((t) => (
          <NearbyTaf key={t.icaoId ?? Math.random()} taf={t} />
        ))}
      </div>
    );
  }

  const t = station[0];
  return (
    <div className="space-y-3">
      <div className="bg-cockpit-700/30 border border-cockpit-700 rounded-md px-3 py-2 font-mono text-xs leading-snug whitespace-pre-wrap">
        {t.rawTAF || t.rawOb || '—'}
      </div>
      <TafPlain taf={t as unknown as TafEnvelope} />
      <div className="flex items-center justify-between text-[10px] text-cockpit-400">
        <span>Issued: {formatZuluWithLocal(t.issueTime)}</span>
        {nearby.length > 0 && <span>{nearby.length} nearby</span>}
      </div>
    </div>
  );
}

/** Plain-text translation block. Renders one row per forecast period
 *  in the TAF, with the change indicator + window labeled. Hidden
 *  when the TAF has no parseable fcsts (older legacy payloads). */
function TafPlain({ taf }: { taf: TafEnvelope }) {
  const periods = plainTextTaf(taf);
  if (!periods || periods.length === 0) return null;
  // Boss flag 2026-05-10: every aviation time should show Zulu plus
  // the user's local time in parens. The 'Issued' and 'Valid' rows
  // here surface that information at the top of the plain-text
  // translation so a pilot doesn't have to mental-math UTC offsets.
  const showIssued = !!taf.issueTime;
  const showValid =
    taf.validTimeFrom != null && taf.validTimeTo != null;
  return (
    <div>
      <div className="text-[10px] tracking-widest text-cockpit-300 mb-1">PLAIN TEXT</div>
      <div className="bg-cockpit-700/20 border border-cockpit-700 rounded-md px-3 py-2 space-y-1.5 text-[12px] text-cockpit-100 leading-snug">
        {(showIssued || showValid) && (
          <div className="pb-1.5 mb-1 border-b border-cockpit-700/70 space-y-0.5">
            {showIssued && (
              <div className="flex gap-2 text-[11px]">
                <span className="text-cockpit-300 w-14 shrink-0">Issued</span>
                <span className="font-mono text-cockpit-100">
                  {formatZuluWithLocal(taf.issueTime)}
                </span>
              </div>
            )}
            {showValid && (
              <div className="flex gap-2 text-[11px]">
                <span className="text-cockpit-300 w-14 shrink-0">Valid</span>
                <span className="font-mono text-cockpit-100">
                  {formatZuluRangeWithLocal(taf.validTimeFrom, taf.validTimeTo)}
                </span>
              </div>
            )}
          </div>
        )}
        {periods.map((line, i) => (
          <div key={i}>{line}</div>
        ))}
      </div>
    </div>
  );
}

function NearbyTaf({ taf }: { taf: TafRow }) {
  const id = taf.icaoId ?? '???';
  const dist = taf._distNm != null ? `${taf._distNm} nm` : '';
  const name = taf.name ? taf.name.replace(/, US$/, '') : '';
  return (
    <div className="space-y-1">
      <div className="flex items-baseline justify-between">
        <span className="font-mono text-sm text-cockpit-100">{id}</span>
        <span className="text-[10px] text-cockpit-400 truncate ml-2">{name} {dist && `· ${dist}`}</span>
      </div>
      <div className="bg-cockpit-700/30 border border-cockpit-700 rounded-md px-3 py-2 font-mono text-[11px] leading-snug whitespace-pre-wrap">
        {taf.rawTAF || taf.rawOb || '—'}
      </div>
      <TafPlain taf={taf as unknown as TafEnvelope} />
    </div>
  );
}

// ── MOS / LAMP panel ────────────────────────────────────────────────────
//
// Renders the GFS-LAMP 38-hour station bulletin (temperature, dewpoint,
// wind, gusts, ceiling/visibility, obstruction-to-vision, precip
// probability). LAMP is hourly-updating MOS-corrected guidance for
// ~2,000 US stations. Only stations in the LAMP network get a
// bulletin — small uncontrolled fields without ASOS / AWOS aren't
// in the network. Shows a clear 'no bulletin' message in that case.
//
// Note (boss 2026-05-26 00:13 PT): LAMP v2.6 (Sep 2024) dropped the
// altimeter row from the bulletin. For altimeter forecasts we use a
// separate source (METAR proxy near-term, GFS surface-pressure GRIB
// long-term — future build). This panel surfaces what LAMP DOES
// publish today.

interface LampHour {
  hr: number;
  utcHour: number;
  tmpF?: number | null;
  dptF?: number | null;
  windDirDeg?: number | null;
  windKt?: number | null;
  windGustKt?: number | null;
  cloud?: string | null;
  ceilingCat?: number | null;
  conditionalCeilingCat?: number | null;
  visCat?: number | null;
  conditionalVisCat?: number | null;
  obstruction?: string | null;
  precipProb1h?: number | null;
  precipProb6h?: number | null;
  precipType?: string | null;
}

interface LampResp {
  ok: boolean;
  error?: string;
  bulletin?: {
    ident: string;
    rawStationName: string;
    issuedAt: string | null;
    issuedRaw: string | null;
    hours: LampHour[];
  };
  raw?: string;
}

function MosPanel({ ident }: { ident: string }) {
  const url = `/api/wx/lamp?ident=${encodeURIComponent(ident)}`;
  const { data, error, loading } = useFetchUrl<LampResp>(url);
  const [showRaw, setShowRaw] = useState(false);
  if (loading) return <Loading />;
  if (error) return <ErrorMsg msg={error} />;
  if (!data?.ok || !data.bulletin) {
    return (
      <NoData
        kind="LAMP MOS"
        hint={data?.error || 'This station may not be in the ~2,000-station LAMP network. Try a nearby reporting field.'}
      />
    );
  }
  const b = data.bulletin;
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between text-[10px] text-cockpit-400">
        <span>
          GFS-LAMP · issued {b.issuedAt ? formatZuluWithLocal(b.issuedAt) : (b.issuedRaw ?? '?')}
        </span>
        <button
          onClick={() => setShowRaw((v) => !v)}
          className="text-ember-400 hover:text-ember-300 underline text-[10px]"
        >
          {showRaw ? 'Decoded' : 'Show raw'}
        </button>
      </div>
      {showRaw && data.raw && (
        <div className="bg-cockpit-700/30 border border-cockpit-700 rounded-md px-2 py-2 font-mono text-[10px] leading-snug whitespace-pre overflow-x-auto">
          {data.raw}
        </div>
      )}
      {!showRaw && <LampHourlyTable hours={b.hours} />}
      <div className="text-[10px] text-cockpit-500 leading-relaxed">
        LAMP v2.6 publishes 38 hours of station-specific MOS-corrected guidance.
        Altimeter setting was dropped from the bulletin in 2024 — the Performance
        section pulls altimeter from current METAR + (long-term) GFS surface-pressure GRIB.
      </div>
    </div>
  );
}

function LampHourlyTable({ hours }: { hours: LampHour[] }) {
  if (hours.length === 0) {
    return <div className="text-xs text-cockpit-400 italic">No forecast hours in bulletin.</div>;
  }
  // Show next 24 hours by default; let the pilot scroll horizontally
  // for the back-end of the 38h window.
  return (
    <div className="overflow-x-auto">
      <table className="text-[10px] font-mono w-full">
        <thead className="text-cockpit-400 sticky top-0">
          <tr>
            <th className="text-left pr-2 py-0.5">UTC</th>
            <th className="text-right px-1 py-0.5" title="Temperature (°F)">T</th>
            <th className="text-right px-1 py-0.5" title="Dewpoint (°F)">Td</th>
            <th className="text-right px-1 py-0.5" title="Wind direction (deg)/speed (kt)/gust">Wind</th>
            <th className="text-right px-1 py-0.5" title="Cloud cover">CLD</th>
            <th className="text-right px-1 py-0.5" title="Ceiling category 1-8">CIG</th>
            <th className="text-right px-1 py-0.5" title="Visibility category 1-7">VIS</th>
            <th className="text-right px-1 py-0.5" title="Obstruction to vision">OBV</th>
            <th className="text-right px-1 py-0.5" title="Precip prob (1h%)">P01</th>
            <th className="text-right px-1 py-0.5" title="Precip type">TYP</th>
          </tr>
        </thead>
        <tbody>
          {hours.map((h, i) => (
            <tr key={i} className="border-t border-cockpit-800">
              <td className="pr-2 py-0.5 text-cockpit-300">{String(h.utcHour).padStart(2, '0')}</td>
              <td className="text-right px-1 py-0.5">{val(h.tmpF)}</td>
              <td className="text-right px-1 py-0.5 text-cockpit-400">{val(h.dptF)}</td>
              <td className="text-right px-1 py-0.5">
                {h.windDirDeg != null && h.windKt != null
                  ? `${pad3(h.windDirDeg)}/${pad2(h.windKt)}${h.windGustKt != null ? `G${h.windGustKt}` : ''}`
                  : '—'}
              </td>
              <td className="text-right px-1 py-0.5 text-cockpit-300">{h.cloud ?? '—'}</td>
              <td className="text-right px-1 py-0.5">{cigLabel(h.ceilingCat)}</td>
              <td className="text-right px-1 py-0.5">{visLabel(h.visCat)}</td>
              <td className="text-right px-1 py-0.5 text-cockpit-300">{h.obstruction ?? '—'}</td>
              <td className="text-right px-1 py-0.5">{h.precipProb1h != null ? `${h.precipProb1h}%` : '—'}</td>
              <td className="text-right px-1 py-0.5 text-cockpit-300">{h.precipType ?? '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function val(n: number | null | undefined): string {
  if (n == null) return '—';
  return String(n);
}
function pad2(n: number): string { return String(n).padStart(2, '0'); }
function pad3(n: number): string { return String(n).padStart(3, '0'); }

/** LAMP ceiling cat: 1<200, 2 200-400, 3 500-900, 4 1000-1900,
 *  5 2000-3000, 6 3100-6500, 7 6600-12000, 8 >12000. */
function cigLabel(c: number | null | undefined): string {
  if (c == null) return '—';
  switch (c) {
    case 1: return '<200';
    case 2: return '2-4';
    case 3: return '5-9';
    case 4: return '10-19';
    case 5: return '20-30';
    case 6: return '31-65';
    case 7: return '66-120';
    case 8: return '>120';
    default: return String(c);
  }
}

/** LAMP vis cat: 1<½, 2 ½-<1, 3 1-<2, 4 2-<3, 5 3-<5, 6 5-6, 7>6 sm. */
function visLabel(v: number | null | undefined): string {
  if (v == null) return '—';
  switch (v) {
    case 1: return '<½';
    case 2: return '½-1';
    case 3: return '1-2';
    case 4: return '2-3';
    case 5: return '3-5';
    case 6: return '5-6';
    case 7: return '>6';
    default: return String(v);
  }
}

function ComingSoon({ source }: { source: string }) {
  return (
    <div className="bg-cockpit-700/20 border border-dashed border-cockpit-600 rounded-md px-3 py-3 text-xs text-cockpit-300">
      <p className="leading-snug">Coming soon.</p>
      <p className="mt-2 text-[10px] text-cockpit-400">Source: {source}</p>
    </div>
  );
}

function Loading() {
  return <div className="text-xs text-cockpit-400">Loading…</div>;
}

function ErrorMsg({ msg }: { msg: string }) {
  return <div className="text-xs text-red-400">⚠ {msg}</div>;
}

function NoData({ kind, hint }: { kind: string; hint?: string }) {
  return (
    <div className="text-xs text-cockpit-400 italic">
      No {kind} on file.{hint ? ` ${hint}` : ''}
    </div>
  );
}


